import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/app/Bits";
import UserTypesEditCard from "@/components/app/UserTypesEditCard";
import { MAX_COVER_BYTES } from "@/lib/constants";
import { toast } from "sonner";
import { Upload, RotateCcw, Check, Sparkles, ShieldCheck, Mail, MessageSquare, Play, User as UserIcon, X, Heart } from "lucide-react";
import { format, parseISO } from "date-fns";
import FeedbackModal from "@/components/app/FeedbackModal";
import CustomActivitiesDialog from "@/components/app/CustomActivitiesDialog";
import { useDogs } from "@/context/DogsContext";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function NameEmailEditCard() {
  // Inline profile editor for the two most-requested fields. Name is
  // free to change; email requires current_password (account-takeover
  // guard). Uses updateProfile from AuthContext so <NavBar>/<Sidebar>
  // pick up the new name immediately.
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // Keep the form in sync when user context refreshes (e.g. after login).
  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
  }, [user?.name, user?.email]);

  const emailChanged = email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
  const nameChanged = name.trim() !== (user?.name || "");
  const dirty = emailChanged || nameChanged;

  const reset = () => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setPassword("");
  };

  const save = async () => {
    if (!dirty) return;
    if (emailChanged && !password) {
      toast.error("Enter your current password to change your email.");
      return;
    }
    setSaving(true);
    try {
      const patch = {};
      if (nameChanged) patch.name = name.trim();
      if (emailChanged) {
        patch.email = email.trim().toLowerCase();
        patch.current_password = password;
      }
      await updateProfile(patch);
      toast.success(emailChanged ? "Profile updated — your new email is active" : "Name updated");
      setPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-border shadow-none rounded-lg" data-testid="profile-name-email-card">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
            <UserIcon size={18} />
          </div>
          <div className="flex-1 min-w-0 space-y-4">
            <div>
              <p className="font-display text-lg font-bold tracking-tight">Your details</p>
              <p className="text-xs text-muted-foreground mt-0.5">Displayed on emails, sharing, and everywhere your name appears in the app.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  autoComplete="name"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  data-testid="profile-email-input"
                />
              </div>
            </div>

            {emailChanged && (
              <div className="rounded-md border border-primary/20 bg-primary/5 p-3" data-testid="profile-email-password-block">
                <Label htmlFor="profile-current-password" className="text-xs uppercase tracking-[0.16em] text-primary font-semibold">
                  Confirm with current password
                </Label>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Since you&apos;re changing your email address, we need your current password. This is the address you&apos;ll use to log in.
                </p>
                <Input
                  id="profile-current-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Current password"
                  autoComplete="current-password"
                  data-testid="profile-current-password-input"
                />
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                onClick={save}
                disabled={saving || !dirty || (emailChanged && !password)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="profile-save"
              >
                <Check size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save changes"}
              </Button>
              {dirty && (
                <Button type="button" variant="ghost" onClick={reset} disabled={saving} data-testid="profile-reset">
                  <X size={14} className="mr-1.5" /> Discard
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function SubscriptionSection() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "monthly" | "yearly" | "polling"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription/me");
      setSub(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pollStatus = useCallback(async (sessionId) => {
    setBusy("polling");
    const maxAttempts = 8;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Subscription activated. Welcome to Pro!");
          await load();
          setBusy(null);
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired. Try again.");
          setBusy(null);
          return;
        }
      } catch (err) {
        // poll failure is fine — keep retrying
        console.debug("Subscription poll attempt failed:", err);
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    toast("Payment is still processing. Refresh in a minute.");
    await load();
    setBusy(null);
  }, [load]);

  // handle ?stripe=success&session_id=... return
  useEffect(() => {
    const stripe = params.get("stripe");
    const sessionId = params.get("session_id");
    if (stripe === "success" && sessionId) {
      pollStatus(sessionId);
      // clear url params
      const next = new URLSearchParams(params);
      next.delete("stripe");
      next.delete("session_id");
      setParams(next, { replace: true });
    } else if (stripe === "cancel") {
      toast("Checkout cancelled");
      const next = new URLSearchParams(params);
      next.delete("stripe");
      setParams(next, { replace: true });
    }
  }, [params, pollStatus, setParams]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/checkout/subscription", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const plans = [
    {
      key: "monthly",
      title: "Pro · Monthly",
      price: "$9",
      period: "/ month",
      sub: "Billed every 30 days",
    },
    {
      key: "yearly",
      title: "Pro · Yearly",
      price: "$90",
      period: "/ year",
      sub: "Save 16% vs monthly",
      badge: "Best value",
    },
  ];

  const benefits = [
    "Unlimited dogs",
    "Email reminders delivered to your inbox",
    "Multi-user sharing (view & edit)",
    "Live Webinars",
    "Yearly Conference (January)",
    "Full access to every section as we add more",
  ];

  const active = sub?.active;
  const freePass = sub?.free_pass_active;
  const freePassUntil = sub?.free_pass_until ? format(parseISO(sub.free_pass_until), "MMMM d, yyyy") : null;

  return (
    <div className="space-y-6" data-testid="subscription-section">
      <SectionTitle overline="Subscription" title={freePass ? "Everything is free right now" : active ? "You're on Pro" : "Upgrade to Pro"} />

      {freePass && (
        <Card className="border-primary/40 shadow-none rounded-lg bg-secondary" data-testid="free-pass-card">
          <CardContent className="p-6 sm:p-8 flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Sparkles size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-2xl font-extrabold tracking-tight" data-testid="free-pass-headline">Everything is free until {freePassUntil || "January 2027"}</p>
              <p className="text-sm text-muted-foreground mt-2">
                During our launch window, every account — Free, Starter, Pro — has full access to every feature. No credit card needed, no trial countdown. Subscribe later when you&apos;re ready.
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : freePass ? null : active ? (
        <Card className="border-border shadow-none rounded-lg bg-secondary" data-testid="subscription-active-card">
          <CardContent className="p-6 sm:p-8 flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <ShieldCheck size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-2xl font-extrabold tracking-tight">Pro · {sub.plan === "yearly" ? "Yearly" : "Monthly"}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Active until {sub.expires_at ? format(parseISO(sub.expires_at), "MMM d, yyyy") : "—"}.
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {benefits.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" /> {b}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-4">
                Want to extend? Pick a plan below — extra time will be added to your current expiry.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border shadow-none rounded-lg" data-testid="subscription-free-card">
          <CardContent className="p-6 sm:p-8 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles size={16} className="text-primary" />
              <span className="font-medium">You&apos;re on the Free plan</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Free includes 1 dog and read-only access to every feature. Upgrade to Pro to unlock:
            </p>
            <ul className="space-y-1 text-sm">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <Check size={14} className="text-primary shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {plans.map((p) => (
          <Card key={p.key} className="border-border shadow-none rounded-lg relative" data-testid={`plan-card-${p.key}`}>
            {p.badge && (
              <span className="absolute -top-2.5 right-4 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 rounded">
                {p.badge}
              </span>
            )}
            <CardContent className="p-6 sm:p-8 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.title}</p>
                <p className="font-display text-4xl font-extrabold tracking-tighter mt-2">
                  {p.price}<span className="text-base font-medium text-muted-foreground ml-1">{p.period}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">{p.sub}</p>
              </div>
              <Button
                onClick={() => subscribe(p.key)}
                disabled={!!busy || freePass}
                data-testid={`subscribe-${p.key}-button`}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {freePass
                  ? "Available after Jan 2027"
                  : busy === p.key ? "Redirecting…" : busy === "polling" ? "Verifying…" : (active ? "Add more time" : "Subscribe")}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Payments are securely processed by Stripe. You&apos;ll be redirected to a hosted checkout page.
      </p>
    </div>
  );
}

function WeeklyDigestSection() {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const send = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/digest/send-test");
      if (data?.ok === false && data?.reason === "no_activity") {
        setLastResult({ kind: "empty", message: data.message });
        toast("Nothing to summarise yet — log some activity first.");
      } else {
        setLastResult({ kind: "sent", totals: data?.totals });
        toast.success("Digest sent to your inbox");
      }
    } catch (e) {
      const status = e.response?.status;
      let detail = formatApiErrorDetail(e.response?.data?.detail) || "Failed";
      // Resend sandbox / upstream email failures often surface as raw Cloudflare strings.
      // Replace with something users can understand.
      if (status === 502 || /cloudflare|origin web server|gateway/i.test(detail)) {
        detail = "Couldn’t send the preview right now — the email service rejected the request. Please try again in a minute.";
      }
      setLastResult({ kind: "error", message: detail });
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="weekly-digest-section">
      <SectionTitle overline="Weekly digest" title="Your Dog Days · Sunday inbox summary" />
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <p className="text-sm text-foreground/80 leading-relaxed">
            Each week we&apos;ll email you a short recap of your dog&apos;s journal entries, completed prompts, weight checks, and upcoming shows — all in one place. Pro feature; honours your unsubscribe.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={send}
              disabled={sending}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="send-digest-preview-button"
            >
              <Mail size={16} className="mr-1.5" />
              {sending ? "Sending…" : "Send a preview to my inbox"}
            </Button>
            {lastResult?.kind === "sent" && lastResult.totals && (
              <span className="text-xs text-muted-foreground" data-testid="digest-totals">
                Summary: {lastResult.totals.journals || 0} journal entries · {lastResult.totals.prompts || 0} prompts · {lastResult.totals.upcoming_shows || 0} upcoming shows
              </span>
            )}
            {lastResult?.kind === "empty" && (
              <span className="text-xs text-muted-foreground" data-testid="digest-empty-message">{lastResult.message}</span>
            )}
            {lastResult?.kind === "error" && (
              <span className="text-xs text-destructive" data-testid="digest-error-message">{lastResult.message}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            The automatic Monday-morning broadcast is gated behind <code className="text-[11px] bg-muted px-1 py-0.5 rounded">WEEKLY_DIGEST_ENABLED</code> in the backend env — flip it on when you&apos;re ready to ship to all users.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomActivitiesSection() {
  const { dogs } = useDogs();
  const [openFor, setOpenFor] = useState(null);   // { id, name } or null
  const [counts, setCounts] = useState({});       // dogId -> number

  const refreshCounts = useCallback(async () => {
    if (!dogs.length) { setCounts({}); return; }
    const next = {};
    await Promise.all(
      dogs.map(async (d) => {
        try {
          const { data } = await api.get(`/dogs/${d.id}/custom-activities`);
          next[d.id] = (data.items || []).length;
        } catch {
          next[d.id] = 0;
        }
      })
    );
    setCounts(next);
  }, [dogs]);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  return (
    <div className="space-y-4">
      <SectionTitle
        overline="Your dogs"
        title="Custom activities"
      />
      <Card className="border-border shadow-none rounded-lg" data-testid="custom-activities-section">
        <CardContent className="p-6 sm:p-8">
          <p className="text-sm text-muted-foreground mb-4">
            Add signature routines per dog. They show up in every activity dropdown — Weekly Planner, event logging, and Reminders.
          </p>
          {dogs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Add a dog first to manage custom activities.</p>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden" data-testid="settings-dogs-list">
              {dogs.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-3 px-4 py-3"
                  data-testid={`settings-custom-activities-row-${d.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{d.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {counts[d.id] || 0} custom {(counts[d.id] || 0) === 1 ? "activity" : "activities"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenFor({ id: d.id, name: d.name })}
                    data-testid={`settings-manage-activities-${d.id}`}
                  >
                    Manage
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {openFor ? (
        <CustomActivitiesDialog
          open={!!openFor}
          onOpenChange={(v) => { if (!v) { setOpenFor(null); refreshCounts(); } }}
          dogId={openFor.id}
          dogName={openFor.name}
          onChanged={refreshCounts}
        />
      ) : null}
    </div>
  );
}




function FeedbackSection() {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-4">
      <SectionTitle overline="Get in touch" title="Contact us · Feedback" />
      <Card className="border-border shadow-none rounded-lg" data-testid="settings-feedback-card">
        <CardContent className="p-6 sm:p-8 flex items-start gap-4 flex-wrap">
          <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <MessageSquare size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium">Have a bug, feature request, or a nice note?</p>
            <p className="text-sm text-muted-foreground mt-1">
              Send it straight to Karin — she reads every one. Include app details (build, page, browser) with a checkbox so triage is faster.
            </p>
          </div>
          <Button type="button" onClick={() => setOpen(true)} data-testid="settings-feedback-open">
            <MessageSquare size={14} className="mr-1.5" /> Send feedback
          </Button>
        </CardContent>
      </Card>
      <FeedbackModal open={open} onOpenChange={setOpen} source="settings" />
    </div>
  );
}


function EmailStatusSection() {
  // Shows a banner + "Resume now" button when the current user is either
  // paused (30-day soft mute) or manually opted out. Hidden entirely for
  // users who aren't in either state — this is a targeted card, not
  // permanent chrome.
  const [state, setState] = useState(null);   // { opted_out, paused_until, ... }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/emails/preferences");
      setState(data);
    } catch (err) {
      // Silent — endpoint only fails when unauthenticated, which shouldn't
      // happen inside Settings.
      setState({ opted_out: false, paused_until: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isPaused = !!state?.paused_until && new Date(state.paused_until) > new Date();
  const isOptedOut = !!state?.opted_out;
  // Hard bounce / complaint opt-outs are deliberately NOT self-resumable —
  // users have to contact us so we can validate the deliverability recovered.
  const reason = (state?.opted_out_reason || "").toLowerCase();
  const isSelfResumable = isPaused || (isOptedOut && !["hard_bounce", "spam_complaint"].includes(reason));

  const resume = async () => {
    setBusy(true);
    try {
      await api.post("/emails/resume");
      toast.success(isPaused ? "Emails resumed" : "You're back on the list");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not resume emails");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  if (!isPaused && !isOptedOut) return null;   // nothing to show

  return (
    <div data-testid="email-status-card">
      <SectionTitle overline="Emails" title={isPaused ? "You're paused" : "You're unsubscribed"} />
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Mail size={20} />
            </div>
            <div className="flex-1 min-w-0">
              {isPaused ? (
                <>
                  <p className="text-sm text-foreground/85 leading-relaxed">
                    All Your Dog Days emails are paused until{" "}
                    <strong data-testid="email-pause-until">
                      {format(parseISO(state.paused_until), "MMMM d, yyyy")}
                    </strong>. Newsletters, reminders, and digests will resume automatically — or you can turn them back on now.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-foreground/85 leading-relaxed">
                    You&apos;re currently opted out of Your Dog Days emails
                    {reason ? <> (<span className="text-muted-foreground">{reason.replace(/_/g, " ")}</span>)</> : null}.
                    {isSelfResumable
                      ? " Click below to opt back in."
                      : " This opt-out was set automatically because a previous email bounced or was reported as spam. To re-enable emails, reply to any past email or contact us."}
                  </p>
                </>
              )}
              {isSelfResumable && (
                <div className="mt-4">
                  <Button
                    onClick={resume}
                    disabled={busy}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="resume-emails-button"
                  >
                    <Play size={14} className="mr-1.5" />
                    {busy ? "Resuming…" : "Resume emails now"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RainbowBridgePreferenceSection() {
  // A gentle, opt-out control for the Rainbow Bridge features. When on, the
  // memorial strip, heart badge, edit-dog toggle, and anniversary email are
  // all hidden. Existing Rainbow-Bridge data stays on the dog records so
  // reactivating restores everything as-is.
  const { user, updateProfile } = useAuth();
  const hidden = !!user?.rainbow_bridge_hidden;
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    setSaving(true);
    try {
      await updateProfile({ rainbow_bridge_hidden: !hidden });
      toast.success(hidden ? "Rainbow Bridge is back on" : "Rainbow Bridge is hidden");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="rainbow-bridge-pref-card">
      <SectionTitle overline="Memory" title="Rainbow Bridge" />
      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
              <Heart size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground/85 leading-relaxed">
                Your Dog Days includes soft memorial features — a "Rainbow Bridge" toggle on the Edit Dog dialog, a memorial strip on the dog overview, and a once-a-year "thinking of you" email on the passing date.
                {" "}If those aren&apos;t what you need right now, you can turn the whole thing off. Nothing gets deleted — your dog&apos;s memory-related fields stay stored, just hidden.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  onClick={toggle}
                  disabled={saving}
                  variant={hidden ? "default" : "outline"}
                  className={hidden ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}
                  data-testid="rainbow-bridge-pref-toggle"
                >
                  {saving ? "Saving…" : hidden ? "Turn Rainbow Bridge back on" : "Hide Rainbow Bridge features"}
                </Button>
                <span className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="rainbow-bridge-pref-state">
                  Currently: {hidden ? "hidden" : "on"}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [cover, setCover] = useState("");
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const { data } = await api.get("/settings/cover");
    setCover(data.cover_url || "");
    setPreview(data.cover_url || "");
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_COVER_BYTES) {
      toast.error("Please choose an image under 3 MB");
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    setPreview(dataUrl);
  };

  const save = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await api.put("/settings/cover", { cover_url: preview });
      toast.success("Cover image updated");
      setCover(preview);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the default cover image?")) return;
    setSaving(true);
    try {
      const { data } = await api.delete("/settings/cover");
      setCover(data.cover_url);
      setPreview(data.cover_url);
      toast.success("Cover reset");
    } finally {
      setSaving(false);
    }
  };

  const dirty = preview && preview !== cover;

  return (
    <div className="space-y-12" data-testid="settings-page">
      <SectionTitle overline="About you" title="Profile" />
      <NameEmailEditCard />
      <UserTypesEditCard overline="Profile" />

      <SubscriptionSection />

      <EmailStatusSection />

      <WeeklyDigestSection />

      <CustomActivitiesSection />

      <RainbowBridgePreferenceSection />

      <FeedbackSection />

      {isAdmin && (
      <>
      <SectionTitle overline="Site settings" title="Login cover image" />

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 sm:p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Preview</Label>
              <div className="aspect-[4/5] bg-muted rounded-md overflow-hidden border border-border">
                {preview ? (
                  <img src={preview} alt="Cover preview" className="w-full h-full object-cover" data-testid="cover-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No image</div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label htmlFor="cover-file">Upload from device</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <Input
                    id="cover-file"
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    data-testid="cover-file-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">JPG or PNG, up to 3 MB. Stored in the database as base64.</p>
              </div>

              <div>
                <Label htmlFor="cover-url">…or paste an image URL</Label>
                <Input
                  id="cover-url"
                  data-testid="cover-url-input"
                  value={preview.startsWith("data:") ? "" : preview}
                  onChange={(e) => setPreview(e.target.value)}
                  placeholder="https://…"
                  className="mt-1.5"
                />
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={save}
                  disabled={!dirty || saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="save-cover-button"
                >
                  <Upload size={16} className="mr-1.5" />
                  {saving ? "Saving…" : "Save as cover"}
                </Button>
                <Button
                  variant="outline"
                  onClick={reset}
                  disabled={saving}
                  data-testid="reset-cover-button"
                >
                  <RotateCcw size={16} className="mr-1.5" />
                  Reset to default
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
